import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";
import type { Mode } from "./types";

const STORAGE_KEY = "uzp_mode";

/** Live unless test mode was chosen in this browser since the last sign-in. */
function readMode(): Mode {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "test" ? "test" : "live";
  } catch {
    return "live";
  }
}

function writeMode(mode: Mode) {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Storage unavailable (private window, blocked site data): the toggle still works for this tab.
  }
}

/** Every sign-in starts in live mode; test mode is a deliberate switch. */
export function resetMode() {
  writeMode("live");
}

interface ModeContextValue {
  mode: Mode;
  setMode: (mode: Mode) => void;
  /** Whether this deployment has test mode at all (production: no). */
  testMode: boolean;
}

const ModeContext = createContext<ModeContextValue | null>(null);

export function ModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(readMode);
  const setMode = useCallback((m: Mode) => {
    writeMode(m);
    setModeState(m);
  }, []);
  const value = useMemo(() => ({ mode, setMode, testMode: true }), [mode, setMode]);
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

/**
 * Below it, a deployment without test mode is live only, whatever was chosen before: the mode
 * reads `live` and can't be switched. Mounted once the account is known (`me.test_mode`).
 */
export function ModeLimit({ testMode, children }: { testMode: boolean; children: ReactNode }) {
  const outer = useMode();
  const value = useMemo(
    () => (testMode ? outer : { mode: "live" as const, setMode: () => {}, testMode: false }),
    [testMode, outer],
  );
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  const ctx = useContext(ModeContext);
  if (!ctx) throw new Error("useMode must be used inside <ModeProvider>");
  return ctx;
}
